// components/MembersList.tsx
import React, { useCallback } from 'react';
import { CheckCircle } from '@/components/ui/lucide-memo';
import { RowMoreButton } from '@/components/ui/RowMoreButton';
import { MemberData } from '#types/types';
import UserAvatar from './UserAvatar';

interface MembersListProps {
  members: MemberData[];
  isViewerAdmin: boolean;
  currentUserId?: string | null;
  onMemberContextMenu?: (member: MemberData, event: React.MouseEvent) => void;
  canOpenContextMenuFor?: (member: MemberData) => boolean;
  avatarTimestamp?: number;
  emptyMessage?: string;
  selectable?: boolean;
  selectedMemberIds?: Set<string>;
  onToggleMember?: (member: MemberData) => void;
}

const getDisplayName = (member: MemberData): string => {
  const parts = [member.firstName, member.lastName].filter((part): part is string => Boolean(part));
  if (parts.length > 0) return parts.join(' ');
  return member.email.split('@')[0] || 'Unknown';
};


interface MemberRowProps {
  member: MemberData;
  isViewerAdmin: boolean;
  currentUserId?: string | null;
  avatarTimestamp?: number;
  selectionEnabled: boolean;
  isSelected: boolean;
  onToggle: (member: MemberData) => void;
  onContextMenu: (member: MemberData, event: React.MouseEvent) => void;
  /** Shows the row's '...' so the member menu is discoverable, not right-click only. */
  canOpenMenu: boolean;
}

const MemberRow = React.memo(function MemberRow({
  member,
  isViewerAdmin,
  currentUserId,
  avatarTimestamp,
  selectionEnabled,
  isSelected,
  onToggle,
  onContextMenu,
  canOpenMenu,
}: MemberRowProps) {
  const displayName = getDisplayName(member);
  const canEditAvatar = isViewerAdmin || currentUserId === member.id;

  const handleClick = useCallback(() => {
    if (selectionEnabled) onToggle(member);
  }, [selectionEnabled, onToggle, member]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    onContextMenu(member, event);
  }, [onContextMenu, member]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!selectionEnabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onToggle(member);
  }, [selectionEnabled, onToggle, member]);

  // ChatGPT's list row (owner, 2026-09-29): one grey line for the detail, a status word instead of
  // coloured dots, admin as a word instead of a yellow crown, and the member menu behind a
  // visible '...' as well as the right-click.
  const details = [
    member.email,
    member.isOrganisationAdmin ? 'Admin' : null,
    `${(member.projectCount ?? 0).toLocaleString('en-US')} projects`,
    `${(member.generationCount ?? 0).toLocaleString('en-US')} generations`,
  ].filter(Boolean).join(' · ');
  return (
    <div
      className={`group flex min-h-[60px] items-center gap-3 rounded-[12px] px-2 py-2 transition-colors ${
        selectionEnabled ? 'cursor-pointer' : 'cursor-default'
      } ${isSelected ? 'bg-[#212121]' : 'hover:bg-[#1A1A1A]'}`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      role={selectionEnabled ? 'checkbox' : undefined}
      aria-checked={selectionEnabled ? isSelected : undefined}
      tabIndex={selectionEnabled ? 0 : undefined}
    >
      <UserAvatar
        userId={member.id}
        externalTimestamp={avatarTimestamp}
        name={displayName}
        canEdit={canEditAvatar}
        isOrganisationAdmin={false}
        size={32}
        shape="circle"
      />

      <div className="min-w-0 flex-1">
        <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '21px' }} title={displayName}>{displayName}</div>
        <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }} title={details}>{details}</div>
      </div>

      {member.status ? (
        <span
          className={`hidden shrink-0 md:inline ${member.status.type === 'live' ? 'text-[#ECECEC]' : 'text-[#8E8E8E]'}`}
          style={{ fontSize: 14 }}
          title={`Status: ${member.status.text}`}
        >
          {member.status.type === 'live' ? 'Online' : member.status.text}
        </span>
      ) : null}

      {selectionEnabled ? (
        <CheckCircle size={18} className={isSelected ? 'shrink-0 text-[#ECECEC]' : 'shrink-0 text-[#444444]'} />
      ) : canOpenMenu ? (
        <RowMoreButton
          label={`Manage ${displayName}`}
          onClick={(event) => { event.stopPropagation(); onContextMenu(member, event); }}
        />
      ) : null}
    </div>
  );
});

const MembersList: React.FC<MembersListProps> = ({
  members,
  isViewerAdmin,
  currentUserId,
  onMemberContextMenu,
  canOpenContextMenuFor,
  avatarTimestamp,
  emptyMessage,
  selectable = false,
  selectedMemberIds,
  onToggleMember,
}) => {
  const handleToggle = useCallback((member: MemberData) => {
    onToggleMember?.(member);
  }, [onToggleMember]);

  const handleContextMenu = useCallback((member: MemberData, event: React.MouseEvent) => {
    if (!onMemberContextMenu) return;
    const canOpen = isViewerAdmin || (canOpenContextMenuFor ? canOpenContextMenuFor(member) : false);
    if (!canOpen) return;
    event.preventDefault();
    event.stopPropagation();
    onMemberContextMenu(member, event);
  }, [onMemberContextMenu, isViewerAdmin, canOpenContextMenuFor]);

  if (!members || members.length === 0) {
    return (
      <p className="py-5 text-[15px] text-[#8E8E8E]">
        {emptyMessage || 'No members found in this organisation.'}
      </p>
    );
  }

  const selectionEnabled = selectable && typeof onToggleMember === 'function';

  return (
    // The rows' hover pad hangs outside the column, so photos sit on the page's left edge.
    <div className="-mx-2 flex flex-col">
      {members.map((member) => (
        <MemberRow
          key={member.id}
          member={member}
          isViewerAdmin={isViewerAdmin}
          currentUserId={currentUserId}
          avatarTimestamp={avatarTimestamp}
          selectionEnabled={selectionEnabled}
          isSelected={Boolean(selectionEnabled && selectedMemberIds?.has(member.id))}
          onToggle={handleToggle}
          onContextMenu={handleContextMenu}
          canOpenMenu={Boolean(onMemberContextMenu) && (isViewerAdmin || Boolean(canOpenContextMenuFor?.(member)))}
        />
      ))}
    </div>
  );
};

export default MembersList;
